import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TipTapImage from "@tiptap/extension-image";
import TipTapLink from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { API_BASE, parseJsonResponse } from "../api";
import { FaArrowLeft, FaPaperPlane, FaImage, FaBold, FaItalic, FaUnderline, FaListUl, FaListOl, FaQuoteLeft, FaLink, FaTimes } from "react-icons/fa";
import "../css/styles.css";

const WriteBlog = () => {
  const navigate = useNavigate();
  const coverRef = useRef(null);
  const inlineRef = useRef(null);
  const token = localStorage.getItem("nyatwende_token");
  const user = JSON.parse(localStorage.getItem("nyatwende_user") || "null");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coverImages, setCoverImages] = useState([]); // File objects
  const [coverPreviews, setCoverPreviews] = useState([]); // preview URLs
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TipTapImage,
      TipTapLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Write your safari story here..." }),
    ],
    content: "",
  });

  // Not logged in
  if (!token || !user) {
    return (
      <div className="wb-page">
        <div className="wb-login-msg">
          <h2>Sign in to Write</h2>
          <p>Use the Sign In button in the navigation bar to get started.</p>
          <Link to="/blogs" className="wb-back"><FaArrowLeft /> Back to Blogs</Link>
        </div>
      </div>
    );
  }

  // Upload files to backend → Cloudinary
  const uploadImages = async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    const res = await fetch(`${API_BASE}/api/upload`, {
      method: "POST",
      headers: { "Authorization": `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    return data.urls;
  };

  // Cover image handlers
  const handleCoverFiles = (e) => {
    const newFiles = Array.from(e.target.files);
    const allFiles = [...coverImages, ...newFiles].slice(0, 10);
    setCoverImages(allFiles);
    setCoverPreviews(allFiles.map((f) => URL.createObjectURL(f)));
  };

  const removeCoverImage = (idx) => {
    const newImages = [...coverImages];
    newImages.splice(idx, 1);
    setCoverImages(newImages);
    setCoverPreviews((prev) => {
      const n = [...prev];
      URL.revokeObjectURL(n[idx]);
      n.splice(idx, 1);
      return n;
    });
  };

  // Upload image and insert inline in editor
  const handleInlineUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = await uploadImages(files);
      urls.forEach((url) => {
        editor?.chain().focus().setImage({ src: url }).run();
        // Ask for optional photo credit
        const credit = window.prompt("Photo credit (optional — leave blank to skip):");
        if (credit && credit.trim()) {
          editor?.chain().focus().insertContent(`<p class="photo-credit">Photo Credit – ${credit.trim()}</p>`).run();
        }
      });
    } catch (err) {
      setMessage("err:Failed to upload image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Submit blog
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    const content = editor?.getHTML() || "";

    if (!title.trim() || !description.trim()) {
      return setMessage("err:Title and description are required.");
    }

    setLoading(true);
    try {
      // Upload cover images
      let imageUrls = "";
      if (coverImages.length > 0) {
        const urls = await uploadImages(coverImages);
        imageUrls = urls.join(" , ");
      }

      const res = await fetch(`${API_BASE}/api/blogs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          content,
          image: imageUrls,
          author: user.name,
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(data.error || "Failed to create blog");

      setMessage("ok:Blog published successfully!");
      setTimeout(() => navigate("/blogs"), 1500);
    } catch (err) {
      setMessage(`err:${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const wordCount = editor?.getText()?.split(/\s+/).filter(Boolean).length || 0;
  const charCount = editor?.getText()?.length || 0;

  return (
    <div className="wb-page">
      {/* Hero */}
      <div className="wb-hero">
        <Link to="/blogs" className="wb-back"><FaArrowLeft /> Back to Blogs</Link>
        <h1>Write a Blog Post</h1>
        <p>Share your safari story, travel tips, or adventure with the Nyatwende community.</p>
      </div>

      {/* Form */}
      <form className="wb-card" onSubmit={handleSubmit}>
        {loading && (
          <div className="wb-overlay">
            <div className="wb-spinner" />
            <span>Publishing...</span>
          </div>
        )}

        {/* Title */}
        <div className="wb-section">
          <h3>Title & Summary</h3>
        </div>
        <div className="wb-field">
          <label>Post Title</label>
          <input className="wb-input" placeholder="e.g. My Unforgettable Masai Mara Experience" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="wb-field">
          <label>Author</label>
          <input className="wb-input" value={user.name} readOnly disabled style={{ background: "#f3f4f6", cursor: "not-allowed", opacity: 0.8 }} />
        </div>
        <div className="wb-field">
          <label>Short Description</label>
          <textarea className="wb-input" rows="2" placeholder="A brief summary that appears on the blog listing..." value={description} onChange={(e) => setDescription(e.target.value)} required />
        </div>

        {/* Content */}
        <div className="wb-section">
          <h3>Content</h3>
          <small>Rich text editor</small>
        </div>
        <div className="wb-field">
          <label>Write your article</label>
          <div className="wb-toolbar">
            <button type="button" onClick={() => editor?.chain().focus().toggleBold().run()} className={editor?.isActive("bold") ? "active" : ""} title="Bold"><FaBold /></button>
            <button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()} className={editor?.isActive("italic") ? "active" : ""} title="Italic"><FaItalic /></button>
            <button type="button" onClick={() => editor?.chain().focus().toggleUnderline().run()} className={editor?.isActive("underline") ? "active" : ""} title="Underline"><FaUnderline /></button>
            <span className="wb-toolbar-sep" />
            <button type="button" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} className={editor?.isActive("heading", { level: 2 }) ? "active" : ""} title="Heading">H2</button>
            <button type="button" onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} className={editor?.isActive("heading", { level: 3 }) ? "active" : ""} title="Subheading">H3</button>
            <span className="wb-toolbar-sep" />
            <button type="button" onClick={() => editor?.chain().focus().toggleBulletList().run()} className={editor?.isActive("bulletList") ? "active" : ""} title="Bullet List"><FaListUl /></button>
            <button type="button" onClick={() => editor?.chain().focus().toggleOrderedList().run()} className={editor?.isActive("orderedList") ? "active" : ""} title="Numbered List"><FaListOl /></button>
            <button type="button" onClick={() => editor?.chain().focus().toggleBlockquote().run()} className={editor?.isActive("blockquote") ? "active" : ""} title="Quote"><FaQuoteLeft /></button>
            <span className="wb-toolbar-sep" />
            <button type="button" onClick={() => inlineRef.current?.click()} className={uploading ? "active" : ""} title="Upload Image">
              {uploading ? "..." : <><FaImage /> </>}
            </button>
            <button type="button" onClick={() => { const url = window.prompt("Link URL:"); if (url) editor?.chain().focus().setLink({ href: url }).run(); }} className={editor?.isActive("link") ? "active" : ""} title="Insert Link"><FaLink /></button>
          </div>
          {/* Hidden file input for inline image uploads */}
          <input ref={inlineRef} type="file" accept="image/*" multiple onChange={handleInlineUpload} style={{ display: "none" }} />
          <EditorContent editor={editor} className="wb-editor" />
          <div className="wb-char-count">{charCount} characters · {wordCount} words · ~{Math.max(1, Math.ceil(wordCount / 200))} min read</div>
        </div>

        {/* Cover Images */}
        <div className="wb-section">
          <h3>Cover Photos</h3>
          <small>Up to 10 images</small>
        </div>
        <div className={`wb-upload-zone${coverImages.length ? " has-files" : ""}`} onClick={() => coverRef.current?.click()}>
          <input ref={coverRef} type="file" accept="image/*" multiple onChange={handleCoverFiles} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
          <span className="wb-upload-icon"><FaImage /></span>
          <div className="wb-upload-text">
            {coverImages.length ? `${coverImages.length} photo${coverImages.length > 1 ? "s" : ""} selected` : <>Click to upload or <strong>drag & drop</strong></>}
          </div>
          <div className="wb-upload-hint">PNG, JPG, WEBP · Max 10 photos · These appear as cover gallery</div>
        </div>

        {coverPreviews.length > 0 && (
          <div className="wb-preview-grid">
            {coverPreviews.map((src, i) => (
              <div key={i} className="wb-preview-thumb">
                <img src={src} alt={`Preview ${i + 1}`} />
                <button type="button" className="wb-preview-remove" onClick={(e) => { e.stopPropagation(); removeCoverImage(i); }}><FaTimes /></button>
              </div>
            ))}
          </div>
        )}

        {/* Submit */}
        <button type="submit" className="wb-submit" disabled={loading}>
          <FaPaperPlane /> Publish Post
        </button>

        {message && (
          <div className={`wb-msg ${message.startsWith("ok:") ? "success" : "error"}`}>
            {message.slice(message.indexOf(":") + 1)}
          </div>
        )}
      </form>
    </div>
  );
};

export default WriteBlog;
